const guides = [
  {
    slug: 'how-to-make-small-decisions-faster',
    title: 'How to make small decisions faster without feeling careless',
    summary: 'A practical method for ending low-stakes overthinking while still respecting your preferences.',
    category: 'Everyday choices',
    readTime: '5 min read',
    updated: 'July 24, 2026',
    intro: 'Small decisions are rarely small because the outcome matters. They feel large because they interrupt momentum. Dinner, outfits, tabs to close, which message to answer first — each one asks for a tiny piece of attention. CalmDecide treats those choices as a design problem: reduce the number of moments where your brain has to restart.',
    sections: [
      {
        heading: 'Separate preference from consequence',
        body: [
          'Before you try to choose, ask one quiet question: “Will this matter next week?” If the honest answer is no, the decision deserves a lighter process. That does not mean the decision is meaningless. It means the cost of deliberation may be higher than the cost of being slightly imperfect.',
          'For small choices, use a two-step rule. First, remove anything that is obviously wrong. Second, let a randomizer pick from the remaining acceptable options. The randomizer is not pretending to be wise; it is giving you permission to move.'
        ]
      },
      {
        heading: 'Use the reroll feeling as information',
        body: [
          'A useful trick appears after the result. If the wheel lands on ramen and your body immediately thinks “actually, tacos,” that reaction is not failure. It is preference surfacing after pressure disappears. In that case, take the information and choose tacos.',
          'The point is not obedience to chance. The point is to expose the quiet preference that was hidden behind equal-looking options.'
        ]
      },
      {
        heading: 'Make a default good enough',
        body: [
          'Create a few personal defaults: a default coffee, a default lunch when busy, a default movie genre when tired, a default outfit formula. Defaults protect attention. You can still override them, but you stop rebuilding your life from scratch every afternoon.',
          'The best default is not the most optimized option. It is the option you can accept repeatedly without resentment.'
        ]
      }
    ]
  },
  {
    slug: 'decision-matrix-guide',
    title: 'A simple decision matrix for big life choices',
    summary: 'How to compare jobs, cities, purchases, and long-term tradeoffs with weighted criteria.',
    category: 'Deep decisions',
    readTime: '7 min read',
    updated: 'July 24, 2026',
    intro: 'A decision matrix is useful when a choice has several dimensions and your mind keeps switching which dimension matters most. It does not replace judgment, but it makes tradeoffs visible. When used carefully, it turns a vague anxious cloud into a structured conversation with yourself.',
    sections: [
      {
        heading: 'Start with real options, not fantasies',
        body: [
          'Write down only options you can actually take. “Wait until everything becomes clear” is usually not an option; it is a delay. If you are comparing Company A and Company B, add “stay where I am” only if staying is genuinely available.',
          'Clear option names matter because they prevent the matrix from becoming a mood board. Each option should describe a concrete path you could begin within a defined time window.'
        ]
      },
      {
        heading: 'Choose criteria before scoring',
        body: [
          'Criteria are the values the decision must serve. For a job choice, common criteria might include compensation, learning, manager quality, commute, stability, autonomy, and energy after work. For a city choice, criteria might include cost, community, career opportunity, climate, safety, and family distance.',
          'Pick four to seven criteria. Fewer than four often hides the complexity. More than seven can create false precision and make the process tiring.'
        ]
      },
      {
        heading: 'Weight what you cannot stop caring about',
        body: [
          'Weights force honesty. If salary is 40 percent of the decision, say so. If health and recovery matter more than prestige right now, give them weight. The total should be 100 percent, not because life is mathematical, but because attention is limited.',
          'After scoring, look at the winner and notice your reaction. Relief is a useful signal. Resistance is also a useful signal. The matrix is not a judge; it is a mirror with numbers.'
        ]
      }
    ]
  },
  {
    slug: 'socratic-questions-for-clarity',
    title: 'Five Socratic questions that make a decision less foggy',
    summary: 'Questions that help separate fear, obligation, evidence, and desire before you commit.',
    category: 'Reflection',
    readTime: '6 min read',
    updated: 'July 24, 2026',
    intro: 'Some decisions stay difficult because the facts are not the main problem. The problem is emotional compression: fear, other people’s expectations, sunk cost, identity, and imagined regret all arrive at once. Socratic questions slow the pileup down.',
    sections: [
      {
        heading: 'Question 1: What would I choose if nobody praised or judged me?',
        body: [
          'This question removes the audience. It does not mean other people never matter, but it shows which part of the decision is yours. Many choices become clearer when status is temporarily taken out of the room.'
        ]
      },
      {
        heading: 'Question 2: What evidence would change my mind?',
        body: [
          'If no evidence could change your mind, you may already have decided. If specific evidence could change your mind, go gather it. This question turns rumination into research and sets an endpoint for information gathering.'
        ]
      },
      {
        heading: 'Question 3: Which regret can I live with?',
        body: [
          'Every meaningful choice contains a possible regret. The goal is not to eliminate regret; it is to choose the regret that fits your values. Regret from trying and learning feels different from regret from staying safe and shrinking.'
        ]
      },
      {
        heading: 'Question 4: What would future me thank me for protecting?',
        body: [
          'This question moves attention away from the loudest short-term feeling and toward continuity. Future you may thank you for protecting health, curiosity, savings, relationships, courage, or peace.'
        ]
      },
      {
        heading: 'Question 5: What is the smallest reversible step?',
        body: [
          'Many decisions become less frightening when converted into a pilot. A conversation, a trial week, a budget draft, or a visit can produce better information than another month of thinking.'
        ]
      }
    ]
  },
  {
    slug: 'avoid-decision-fatigue',
    title: 'How to avoid decision fatigue in a busy week',
    summary: 'A calm system for preserving attention when every day asks for too many choices.',
    category: 'Mental energy',
    readTime: '6 min read',
    updated: 'July 24, 2026',
    intro: 'Decision fatigue is not a character flaw. It is what happens when your attention is spent on too many open loops. A busy week turns ordinary choices into background noise, and the noise makes important judgment harder.',
    sections: [
      {
        heading: 'Batch choices by type',
        body: [
          'Instead of deciding meals, errands, messages, and purchases one by one all week, batch them. Pick three easy meals on Sunday. Choose two errand windows. Decide which messages can wait until a fixed hour. Batching reduces context switching, which is often the hidden cost.'
        ]
      },
      {
        heading: 'Use “decide once” rules',
        body: [
          'A decide-once rule is a personal policy for recurring choices. For example: “If I am tired after 7 p.m., I cook the simplest meal available.” “If a meeting has no agenda, I ask for one before accepting.” “If a purchase is non-urgent, I wait 24 hours.”',
          'These rules are not rigid. They are kindness in advance.'
        ]
      },
      {
        heading: 'Protect the high-quality hour',
        body: [
          'Most people have a part of the day when thinking is cleaner. Reserve that hour for decisions that deserve you at your best. Do not spend it comparing socks, reorganizing apps, or scanning options you do not need.'
        ]
      }
    ]
  },
  {
    slug: 'when-to-ask-other-people',
    title: 'When to ask other people for help deciding',
    summary: 'How to use outside opinions without outsourcing your life.',
    category: 'Social clarity',
    readTime: '5 min read',
    updated: 'July 24, 2026',
    intro: 'Advice can be a lantern or a fog machine. The difference is whether you ask the right people the right kind of question. CalmDecide’s community tools are designed for nudges, not for surrendering responsibility.',
    sections: [
      {
        heading: 'Ask for observations, not commands',
        body: [
          'Instead of “What should I do?” ask “What tradeoff am I missing?” or “Which option seems more aligned with what I said I wanted?” Observations help you see. Commands can make you dependent or defensive.'
        ]
      },
      {
        heading: 'Match the person to the decision',
        body: [
          'For a style choice, strangers can be useful because the stakes are low and first impressions matter. For a career or relationship decision, ask people who understand your values, constraints, and patterns. Expertise and closeness are different kinds of signal.'
        ]
      },
      {
        heading: 'Notice whose opinion creates calm',
        body: [
          'Good advice often makes the next step feel more grounded, even if the decision is still hard. Bad advice may create urgency, shame, or a need to perform. Choose advisors who return you to yourself.'
        ]
      }
    ]
  },
  {
    slug: 'reviewing-past-decisions',
    title: 'How to review a past decision without beating yourself up',
    summary: 'A gentle retrospective for learning from choices after enough time has passed.',
    category: 'Review',
    readTime: '6 min read',
    updated: 'July 24, 2026',
    intro: 'A decision review is not a trial. It is a way to update your inner map. Three months later, many choices feel smaller, some feel wiser than expected, and a few reveal patterns you can learn from without turning against yourself.',
    sections: [
      {
        heading: 'Judge the process before the outcome',
        body: [
          'Outcomes contain luck. Process contains habits. Ask whether you used the information available at the time, whether you listened to the right constraints, and whether you acted with reasonable care. A good process can still produce a disappointing result.'
        ]
      },
      {
        heading: 'Name the hidden value',
        body: [
          'Every choice protects something: freedom, stability, belonging, rest, growth, money, curiosity, dignity. When reviewing a decision, name what it was trying to protect. This helps you understand yourself instead of merely grading yourself.'
        ]
      },
      {
        heading: 'Convert regret into a rule',
        body: [
          'If regret appears, turn it into a future rule. “Next time I will talk to two people in the role before accepting.” “Next time I will sleep before sending the final answer.” A rule is regret composted into wisdom.'
        ]
      }
    ]
  }
]

export default guides
